import * as Sentry from "@sentry/react";
import axios from "axios";
import type { GetProjectsResponse } from "@theirstory/common/api_types/backend";
import type {
  ProjectMetadataSchema,
  FieldsLibraryEntry,
  FieldsLibrarySource,
  BulkImportColumnDef,
  BulkImportRecording,
  BulkImportTestimonialFieldInput,
  BulkImportValidationResult,
  BulkImportCommitRow,
  BulkImportCommitResponse,
  ProjectExportColumn,
} from "@theirstory/common/api_types/backend/metadata";
import { createAction } from "@reduxjs/toolkit";

import type { ActionTypeHelper } from "../state/rootStore";
import type { PermissionsProject } from "../../Pages/Projects/Modals/CreateNewProjectModal";

import { notifyError, notifySuccess } from "./notify";
import {
  PROJECTS_LOADED,
  PROJECT_DETAILS_LOADED,
  PROJECT_DETAILS_ERROR,
  PROJECT_STORIES_LOADED,
  PROJECT_STORIES_LOADING,
  PROJECT_STORIES_SET_PAGE_VIEW,
  PROJECT_TABLE_STORIES_LOADED,
  PROJECT_TABLE_STORIES_LOADING,
  PROJECT_STORIES_SORT_SETTING,
  PROJECT_STORIES_FILTER_SETTING,
  PROJECT_TABLE_STORIES_SET_PAGE_VIEW,
  PROJECTS_RESET_CURRENT,
  PROJECTS_LOADING,
  PROJECTS_RESET_USER_PROJECTS,
  PROJECTS_RESET_STORIES,
} from "./types";
import { getProjectFolders, loadFolderStories, resetFolderStories } from "./folders";

export type SessionTypeFilter = "all" | "interview" | "testimonial";
export type TranscriptStatusFilter = "all" | "has_transcript" | "no_transcript";
export type DurationFilter = "all" | "under_15" | "15_to_30" | "30_to_60" | "over_60";
export type ReleaseFormsFilter = "all" | "has_release" | "no_release";

export interface MetadataFieldFilter {
  columnId: string;
  label: string;
  fieldKey?: string;
  answerType?: string;
  selectedValues?: string[];
  startDate?: string;
  endDate?: string;
  textSearch?: string;
}

export interface ProjectStoriesFilters {
  sessionType: SessionTypeFilter;
  tags: string[];
  participants: string[];
  creationDateStart: string;
  creationDateEnd: string;
  duration: DurationFilter;
  releaseForms: ReleaseFormsFilter;
  transcriptStatus: TranscriptStatusFilter;
  metadataFilters: Record<string, MetadataFieldFilter>;
}

export const getAllProjects = (): ActionTypeHelper<Promise<void>> => async (dispatch) => {
  try {
    dispatch({ type: PROJECTS_LOADING });
    const { data } = await axios.get<GetProjectsResponse>("/projects");
    dispatch(projectsLoaded(data));
  } catch (err) {
    console.log(err);
    Sentry.captureException(err);
  }
};

export const getOrgProjects =
  (organizationId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING });
      const { data } = await axios.get<GetProjectsResponse>(`/organization/${organizationId}/projects`);
      dispatch(projectsLoaded(data));
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  };

export const getProject =
  (projectId: string, skipStoriesLoad?: boolean): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    dispatch(loadProjectDetails(projectId));
    dispatch(getProjectFolders(projectId));
    // Skip loading stories if skipStoriesLoad is true (e.g., when there's an active search
    // that will be handled by a separate effect to avoid double loading)
    if (!skipStoriesLoad) {
      dispatch(loadProjectStories({ projectId }));
    }
  };

export const loadProjectDetails = (projectId: string) => async (dispatch) => {
  try {
    if (projectId !== null) {
      const { data } = await axios.get(`/projects/${projectId}`);
      dispatch({ type: PROJECT_DETAILS_LOADED, payload: { projectDetails: data } });
    }
  } catch (error) {
    console.log(error);
    dispatch({ type: PROJECT_DETAILS_ERROR, payload: { status: error?.response?.status } });
    throw error;
  }
};

export const updateProjectData = (updateData: any) => async (dispatch, getState) => {
  const {
    projects: {
      project: { projectDetails },
    },
  } = getState();

  const updatedProjectDetails = {
    ...projectDetails,
    project: {
      ...projectDetails.project,
      ...updateData,
    },
  };

  dispatch({ type: PROJECT_DETAILS_LOADED, payload: { projectDetails: updatedProjectDetails } });
};

let activeStoriesAbortController: AbortController | null = null;

// Response of the project search endpoints: one item per hit (a story plus the
// matched chunk's highlight/transcription, start_time/end_time, speaker, chapters).
export interface ProjectSearchResponse {
  items: any[];
  total: number;
  matches?: unknown;
}

// Project thematic search (v2) — the Sessions/Hub search, shared with the Discover panel.
export const searchProjectThematic = async (
  projectId: string,
  query: string,
  { offset = 0, pageSize, signal }: { offset?: number; pageSize: number; signal?: AbortSignal }
): Promise<ProjectSearchResponse> => {
  const res = await axios.post(
    "/knowledge-service/search/enriched",
    {
      query,
      project_id: projectId,
      offset,
      pageSize,
      mode: "hybrid",
      alpha: 0.5, // keeping it inthe middle for now to test basic results
      minScore: 0.3, // Added this to only show results with >= 30% relevance
    },
    { signal }
  );
  return res.data;
};

// Project stories page; with `search`, the keyword search (v1) with enriched
// transcript matches — the Sessions/Hub search, shared with the Discover panel.
export const searchProjectKeyword = async (
  projectId: string,
  search: string | null,
  {
    page,
    pageSize,
    sort,
    direction,
    filters,
    signal,
  }: {
    page: number;
    pageSize: number;
    sort?: string;
    direction?: string;
    filters?: ProjectStoriesFilters | null;
    signal?: AbortSignal;
  }
): Promise<ProjectSearchResponse> => {
  const urlPath = search ? `/projects/${projectId}/stories/all` : `/projects/${projectId}/stories`;
  const res = await axios.get(urlPath, {
    params: {
      pageSize,
      page,
      search,
      sort,
      direction,
      // Enable enrichment (transcript matches, chapters, speaker) for v1 search
      ...(search ? { enriched: "true" } : {}),
      ...buildFilterQueryParams(filters),
    },
    signal,
  });
  return res.data;
};

export const loadProjectStories =
  (params: { projectId: string; searchMode?: "v1" | "v2"; filters?: ProjectStoriesFilters | null; page?: number }) =>
  async (dispatch, getState) => {
    if (activeStoriesAbortController) {
      activeStoriesAbortController.abort();
    }
    const controller = new AbortController();
    activeStoriesAbortController = controller;
    const signal = controller.signal;

    try {
      const {
        projects: {
          project: {
            paging: { size: pageSize, current: currentPage },
            sort: { column, order },
            filters: stateFilters,
          },
        },
      } = getState();
      const { projectId, searchMode = "v1" } = params;
      const activeFilters = params.filters !== undefined ? params.filters : stateFilters;
      const searchParam = new URLSearchParams(window.location.search).get("search");

      const targetPage = params.page !== undefined ? params.page : params.filters !== undefined ? 1 : currentPage;

      // We put an skeleton loader when we are loading the first page, after that is handled by the video card list
      if (targetPage === 1) {
        dispatch({ type: PROJECT_STORIES_LOADING });
      }

      // Use semantic search (v2) when explicitly requested AND there's a search query
      if (searchMode === "v2" && searchParam) {
        const currentStories = getState().projects.project.stories;
        // Use offset-based pagination for infinite scroll
        const currentOffset = targetPage === 1 ? 0 : currentStories.length;

        const {
          items: newStories,
          total,
          matches,
        } = await searchProjectThematic(projectId, searchParam, {
          offset: currentOffset,
          pageSize,
          signal,
        });
        // Replace stories on first load (offset 0), append for infinite scroll
        const stories = targetPage === 1 ? [...newStories] : [...currentStories, ...newStories];

        // Calculate next page for infinite scroll tracking
        const hasMore = stories.length < total;
        // Keep page tracking for compatibility, but use offset for actual pagination
        const nextPage = hasMore ? targetPage + 1 : targetPage;

        if (activeStoriesAbortController === controller) {
          activeStoriesAbortController = null;
        }

        dispatch({ type: PROJECT_STORIES_LOADED, payload: { stories, total, page: nextPage, projectId, matches } });
        return stories;
      }

      // Use traditional MongoDB search (v1)
      const {
        items: newStories,
        total,
        matches,
      } = await searchProjectKeyword(projectId, searchParam, {
        page: targetPage,
        pageSize,
        sort: column,
        direction: order,
        filters: activeFilters,
        signal,
      });
      // Replace stories on first page, append for pagination
      const stories = targetPage === 1 ? [...newStories] : [...getState().projects.project.stories, ...newStories];

      let page = targetPage;
      if (stories.length < total) {
        page = targetPage + 1;
      }

      if (activeStoriesAbortController === controller) {
        activeStoriesAbortController = null;
      }

      dispatch({ type: PROJECT_STORIES_LOADED, payload: { stories, total, page, projectId, matches } });
      return stories;
    } catch (err) {
      if (axios.isCancel(err)) {
        // Request was cancelled, silently ignore to prevent crash/error logs
        return [];
      }
      if (activeStoriesAbortController === controller) {
        activeStoriesAbortController = null;
      }
      console.log(err);
      throw err;
    }
  };

export const setProjectStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch) => {
    dispatch({ type: PROJECT_STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
  };

export const setProjectStoriesSort = (column, order) => async (dispatch) => {
  dispatch({ type: PROJECT_STORIES_SORT_SETTING, payload: { column, order } });
};

export const setProjectStoriesFilters = (filters: ProjectStoriesFilters | null) => async (dispatch) => {
  dispatch({ type: PROJECT_STORIES_FILTER_SETTING, payload: { filters } });
};

/**
 * Serialize active filters into axios query params. Shared by project and folder story loaders
 * so both pages speak the same filter contract.
 */
export const buildFilterQueryParams = (filters?: ProjectStoriesFilters | null): Record<string, string> => {
  if (!filters) return {};
  const params: Record<string, string> = {};
  if (filters.sessionType && filters.sessionType !== "all") params.sessionType = filters.sessionType;
  if (filters.transcriptStatus && filters.transcriptStatus !== "all")
    params.transcriptStatus = filters.transcriptStatus;
  if (filters.tags && filters.tags.length > 0) params.tags = JSON.stringify(filters.tags);
  if (filters.participants && filters.participants.length > 0)
    params.participants = JSON.stringify(filters.participants);
  if (filters.creationDateStart) params.creationDateStart = filters.creationDateStart;
  if (filters.creationDateEnd) params.creationDateEnd = filters.creationDateEnd;
  if (filters.duration && filters.duration !== "all") params.duration = filters.duration;
  if (filters.releaseForms && filters.releaseForms !== "all") params.releaseForms = filters.releaseForms;
  if (filters.metadataFilters && Object.keys(filters.metadataFilters).length > 0) {
    params.metadataFilters = JSON.stringify(filters.metadataFilters);
  }
  return params;
};

export interface ProjectFilterFacetsResponse {
  tags: string[];
  participants?: string[];
  testimonialFields: Array<{
    key: string;
    label: string;
    answerType?: string;
    options?: string[];
  }>;
  activeDeletedMetadataKeys?: string[];
}

export const loadProjectFilterFacets =
  (projectId: string): ActionTypeHelper<Promise<ProjectFilterFacetsResponse>> =>
  async () => {
    try {
      const { data } = await axios.get<ProjectFilterFacetsResponse>(`/projects/${projectId}/filter-facets`);
      return data;
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "sessions_table_filters");
        scope.setTag("method", "loadProjectFilterFacets");
        scope.setTag("projectId", projectId);
        Sentry.captureException(error);
      });
      throw error;
    }
  };

export const loadProjectMetadataOptions =
  (projectId: string, columnId: string, fieldKey?: string): ActionTypeHelper<Promise<string[]>> =>
  async () => {
    try {
      const { data } = await axios.get<{ options: string[] }>(`/projects/${projectId}/metadata-options`, {
        params: { columnId, fieldKey },
      });
      return Array.isArray(data?.options) ? data.options : [];
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "sessions_table_filters");
        scope.setTag("method", "loadProjectMetadataOptions");
        scope.setTag("projectId", projectId);
        Sentry.captureException(error);
      });
      throw error;
    }
  };

export const loadProjectTableStories =
  (params: any = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        projects: {
          project: {
            projectId: currentProjectId,
            table: {
              paging: { size: pageSize, current: currentPage },
            },
          },
        },
      } = getState();

      const { projectId, clear } = params;
      const newProjectId = projectId !== undefined ? projectId : currentProjectId;
      dispatch({ type: PROJECT_TABLE_STORIES_LOADING, payload: { clear: clear || currentProjectId !== newProjectId } });

      const page = currentProjectId === newProjectId ? currentPage : 1;
      const urlPath = `/projects/${newProjectId}/stories/all`;

      const res = await axios.get(urlPath, { params: { pageSize, page } });

      const { items: stories, total } = res.data;

      dispatch({ type: PROJECT_TABLE_STORIES_LOADED, payload: { stories, total, page } });
      return stories;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

export const setProjectTableStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch, getState) => {
    const {
      projects: {
        project: {
          table: {
            stories,
            paging: { current, size },
          },
        },
      },
    } = getState();

    dispatch({ type: PROJECT_TABLE_STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });

    if (current !== 1 || stories.length === size || pageSize < stories.length) {
      dispatch(loadProjectTableStories());
    }
  };

interface InviteUserProject {
  email: string;
  role: PermissionsProject;
}

export const createProject =
  (name: string, description: string, invites: InviteUserProject[], projectBackgroundUrl: string, orgCode: string) =>
  async () => {
    try {
      await axios.post("/projects/create", {
        name,
        description,
        invites,
        projectBackgroundUrl,
        org_code: orgCode,
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

export const editProject = (projectId: string, values) => async () => {
  try {
    const res = await axios.put(`/projects/${projectId}/edit`, values);
    return res.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const leaveProject = (projectId: string) => async () => {
  try {
    const res = await axios.delete(`/projects/${projectId}/removeme`);
    return res.data;
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
    throw error;
  }
};

export const addStoryToFolders = (storyId: string, foldersId: string[]) => async (dispatch) => {
  const allFoldersRemoved = foldersId.length === 0;
  try {
    await axios.patch(`/stories/${storyId}/edit`, {
      foldersId: foldersId,
    });

    if (allFoldersRemoved) {
      dispatch(notifySuccess(`The interviews have been removed from folder/s`));
    } else {
      dispatch(notifySuccess(`Successfully updated interview into folder/s`));
    }
  } catch (error) {
    dispatch(notifyError(`Error adding interview to folder/s`));
    console.log(error);
  }
};

export const addBulkStoryToFolders = (selectedStoriesIds: string[], foldersId: string[]) => async (dispatch) => {
  const allFoldersRemoved = foldersId.length === 0;

  try {
    await Promise.all(selectedStoriesIds.map((storyId) => axios.patch(`/stories/${storyId}/edit`, { foldersId })));

    const successMessage = allFoldersRemoved
      ? `The interviews have been removed from folder/s`
      : `Successfully updated interviews into folder/s`;
    dispatch(notifySuccess(successMessage));
  } catch (error) {
    dispatch(notifyError(`Error adding interviews to folder/s`));
    console.error("Error in addBulkStoryToFolders:", error);
  }
};

export const moveBulkStoriesToProject =
  (sourceProjectId: string, targetProjectId: string, storyIds: string[]): ActionTypeHelper<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.post(`/projects/${sourceProjectId}/move-stories-to-project`, {
        targetProjectId,
        storyIds,
      });

      dispatch(
        notifySuccess(
          `Moved ${data?.movedCount ?? storyIds.length} interview${
            storyIds.length === 1 ? "" : "s"
          } to the selected project`
        )
      );
      return true;
    } catch (error) {
      dispatch(notifyError("Error moving interviews to the selected project"));
      console.error("Error in moveBulkStoriesToProject:", error);
      return false;
    }
  };

export const resetCurrentProject = () => async (dispatch) => {
  dispatch({ type: PROJECTS_RESET_CURRENT });
};

export const resetStoriesProject = () => async (dispatch) => {
  dispatch({ type: PROJECTS_RESET_STORIES });
};

export const resetUserProjects = () => async (dispatch) => {
  dispatch({ type: PROJECTS_RESET_USER_PROJECTS });
};

interface ProjectMetadataSchemaResponse {
  schema: ProjectMetadataSchema;
  usesDefault: boolean;
}

export const fetchProjectMetadataSchema =
  (projectId: string): ActionTypeHelper<Promise<ProjectMetadataSchemaResponse | null>> =>
  async () => {
    try {
      const { data } = await axios.get<ProjectMetadataSchemaResponse>(`/projects/${projectId}/metadata-schema`);
      return data;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      return null;
    }
  };

export const saveProjectMetadataSchema =
  (
    projectId: string,
    metadataSchema: ProjectMetadataSchema
  ): ActionTypeHelper<Promise<ProjectMetadataSchemaResponse>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.put<ProjectMetadataSchemaResponse>(`/projects/${projectId}/metadata-schema`, {
        metadata_schema: metadataSchema,
      });
      return data;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Could not save metadata schema"));
      throw error;
    }
  };

export const fetchFieldsLibrary =
  (
    projectId: string,
    source: FieldsLibrarySource = "project_metadata"
  ): ActionTypeHelper<Promise<FieldsLibraryEntry[]>> =>
  async () => {
    try {
      const { data } = await axios.get<{ fields: FieldsLibraryEntry[] }>(
        `/projects/${projectId}/metadata-schema/fields-library`,
        { params: { source } }
      );
      return data.fields;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      return [];
    }
  };

// Folder-scoped export/import endpoints only cover that folder's stories.
const bulkImportBasePath = (projectId: string, folderId?: string): string =>
  folderId ? `/folders/${folderId}` : `/projects/${projectId}`;

export const downloadBulkImportTemplate =
  (projectId: string, folderId?: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = await axios.get(`${bulkImportBasePath(projectId, folderId)}/bulk-import/template`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        folderId ? `folder_${folderId}_metadata_template.csv` : `project_${projectId}_metadata_template.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Could not download bulk import template"));
    }
  };

export const fetchExportColumns =
  (projectId: string, folderId?: string): ActionTypeHelper<Promise<ProjectExportColumn[]>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.get<{ columns: ProjectExportColumn[] }>(
        `${bulkImportBasePath(projectId, folderId)}/export/columns`
      );
      return data.columns;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Could not load the export columns"));
      return [];
    }
  };

export const exportProjectTable =
  (projectId: string, columnIds: string[], folderId?: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    try {
      const res = await axios.get(`${bulkImportBasePath(projectId, folderId)}/export`, {
        params: { columns: columnIds.join(",") },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", folderId ? `folder_${folderId}_export.csv` : `project_${projectId}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Could not export project table"));
    }
  };

export const validateBulkImportCsv =
  (projectId: string, file: File, folderId?: string): ActionTypeHelper<Promise<BulkImportValidationResult>> =>
  async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post<BulkImportValidationResult>(
        `${bulkImportBasePath(projectId, folderId)}/bulk-import/validate`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return data;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      const serverMessage =
        axios.isAxiosError(error) && typeof error.response?.data?.error === "string"
          ? (error.response.data.error as string)
          : null;
      dispatch(notifyError(serverMessage || "Failed to validate file"));
      throw error;
    }
  };

export const fetchTestimonialFields =
  (projectId: string): ActionTypeHelper<Promise<BulkImportColumnDef[]>> =>
  async () => {
    try {
      const { data } = await axios.get<{ fields: BulkImportColumnDef[] }>(`/projects/${projectId}/testimonial-fields`);
      return data.fields;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      return [];
    }
  };

export const fetchBulkImportRecordings =
  (projectId: string, folderId?: string): ActionTypeHelper<Promise<BulkImportRecording[]>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.get<{ recordings: BulkImportRecording[] }>(
        `${bulkImportBasePath(projectId, folderId)}/bulk-import/recordings`
      );
      return data.recordings;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Could not load this project's recordings"));
      return [];
    }
  };

export const createTestimonialField =
  (projectId: string, input: BulkImportTestimonialFieldInput): ActionTypeHelper<Promise<BulkImportColumnDef | null>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.post<{ field: BulkImportColumnDef }>(
        `/projects/${projectId}/testimonial-fields`,
        input
      );
      return data.field;
    } catch (error) {
      // The campaign isn't available in this project, so the caller skips the values meant for it.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Failed to add the field to the campaign"));
      throw error;
    }
  };

export const commitBulkImport =
  (
    projectId: string,
    rows: BulkImportCommitRow[],
    folderId?: string
  ): ActionTypeHelper<Promise<BulkImportCommitResponse>> =>
  async (dispatch) => {
    try {
      const { data } = await axios.post<BulkImportCommitResponse>(
        `${bulkImportBasePath(projectId, folderId)}/bulk-import/commit`,
        { rows }
      );
      dispatch(notifySuccess(data.message || `Successfully updated ${data.updatedStoriesCount} stories`));
      if (folderId) {
        dispatch(resetFolderStories());
        dispatch(loadFolderStories({ folderId }));
      } else {
        dispatch(resetStoriesProject());
        dispatch(loadProjectStories({ projectId }));
      }
      return data;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      dispatch(notifyError("Failed to import metadata into project"));
      throw error;
    }
  };

export type ProjectsLoaded = GetProjectsResponse;
export const projectsLoaded = createAction<ProjectsLoaded>(PROJECTS_LOADED);

// export const clearStories = createAction();
